<template>
  <template v-if="isDesktopWide">
    <DesktopFrame v-if="!showDesktopSplash" />
    <DesktopSplash
      v-if="showDesktopSplash"
      @dismiss="showDesktopSplash = false"
    />
  </template>
  <template v-else>
    <router-view v-slot="{ Component }">
      <keep-alive include="search">
        <component :is="Component" :key="$route.path" />
      </keep-alive>
    </router-view>

    <Teleport to="body">
      <div
        v-if="collabInvite"
        class="collab-invite-overlay"
        @click.self="declineCollab"
      >
        <div class="collab-invite-card">
          <div class="collab-invite-icon">✏️</div>
          <p class="collab-invite-from">@{{ collabInvite.initiator }}</p>
          <p class="collab-invite-msg">wants to draw with you!</p>
          <div class="collab-invite-actions">
            <button class="collab-decline-btn" @click="declineCollab">
              Decline
            </button>
            <button class="collab-accept-btn" @click="acceptCollab">
              Draw Together
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <Transition name="sw-update-modal">
        <div v-if="swUpdateWaiting" class="sw-update-overlay" role="dialog" aria-modal="true" aria-label="Update available">
          <div class="sw-update-modal">
            <div class="sw-update-icon">✦</div>
            <h2 class="sw-update-title">New version ready</h2>
            <p class="sw-update-body">A fresh update is waiting — tap below to reload and get the latest.</p>
            <button class="sw-update-cta" @click="applySwUpdate">Update now</button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="offline-banner">
        <div v-if="$store.getters['network/isOffline']" class="offline-banner" role="status" aria-live="polite">
          <span class="offline-banner-dot"></span>
          <span class="offline-banner-msg">You're offline. Scrawls will load when you're back.</span>
        </div>
      </Transition>
    </Teleport>
  </template>
</template>

<script>
import Parse from "parse";
import { getSocket, teardownSocket } from "./utils/socket";
import { hydrateAvatars } from "./utils/avatarHydration";
import {
  getLastFeedVisitMs,
} from "./utils/feedSeen";
import { clearBadge } from "./utils/appBadge";
import {
  reconcilePushSubscription,
  rebindPushIfPWAContextChanged,
} from "./utils/webPush";
import DesktopFrame from "./components/DesktopFrame.vue";
import DesktopSplash from "./components/DesktopSplash.vue";

function detectDesktopWide() {
  if (typeof window === "undefined") return false;
  if (new URLSearchParams(window.location.search).has("_phoneview")) return false;
  return (
    window.matchMedia("(pointer: fine)").matches &&
    window.matchMedia("(min-width: 600px)").matches
  );
}

function shouldShowDesktopSplash() {
  try {
    return localStorage.getItem("scrawl_desktop_splash_seen") !== "true";
  } catch {
    return true;
  }
}

export default {
  name: "app",
  components: { DesktopFrame, DesktopSplash },
  data() {
    return {
      isDesktopWide: detectDesktopWide(),
      showDesktopSplash: shouldShowDesktopSplash(),
      subscription: null,
      subscriptionSetup: null,
      collabInviteSubscription: null,
      collabInviteSubscriptionSetup: null,
      collabInvite: null,
      swUpdateWaiting: null,
      wakeLock: null,
    };
  },
  mounted() {
    window.addEventListener(
      "scrawl-sw-update-available",
      this.onSwUpdateAvailable
    );
    window.addEventListener("online", this.onNetworkOnline);
    window.addEventListener("offline", this.onNetworkOffline);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.acquireWakeLock();
    // First foreground after cold start — clears any unread Scrawl OS
    // notification still hanging in the system tray (user opened the app
    // via the launcher icon, not by tapping the notification).
    this.clearActiveScrawlNotifications();
  },
  beforeUnmount() {
    window.removeEventListener(
      "scrawl-sw-update-available",
      this.onSwUpdateAvailable
    );
    window.removeEventListener("online", this.onNetworkOnline);
    window.removeEventListener("offline", this.onNetworkOffline);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.wakeLock) {
      this.wakeLock.release().catch(() => {});
      this.wakeLock = null;
    }
  },
  async created() {
    this.stripPwaParam();
    this.maybeRedirectToPWA();
    if (this.isDesktopWide) return;

    // F-16: SW registration moved to src/registerServiceWorker.js (imported
    // from main.js) so the precache + update lifecycle live in one place.

    const user = this.$store.getters["auth/currentUser"];
    if (user) {
      try {
        await user.fetch();
        // F-20: hydrate the current user's custom avatar into the in-memory
        // cache before any view renders, so the first paint of Profile /
        // Feed / Search shows the saved avatar instead of the identicon.
        hydrateAvatars(user);
        // F-19: single launch-time `lastSeen` write replaces the deleted
        // 60s heartbeat. Sufficient for F-05 active-user cohorts.
        user.set("lastSeen", Date.now());
        user.save().catch(() => {});
        // F-19: open the authenticated socket; being connected IS the
        // presence signal that gates `⚡` invites server-side.
        getSocket();
        await this.setupNotificationSubscription(user);
        await this.setupCollabInviteSubscription(user);
        // Fire-and-forget: drops the "www.scrawl.ink" chip from OS push
        // banners by re-binding the existing browser-context subscription
        // to the installed-PWA context. Self-flags after the first run.
        rebindPushIfPWAContextChanged();
        // Light the Feed-tab dot from server state in case pushes arrived
        // while the app was closed (LiveQuery only covers the live window).
        this.refreshFeedDotInitial(user);
        // Repair the OS push subscription if it was lost since last boot
        // (SW update, push-service rotation, server-side 404/410 cleanup).
        // Silent — only acts when the user has previously granted permission,
        // so there's no permission prompt and no surprise for opted-out users.
        // This is what makes "open the app once" implicitly heal a dead
        // subscription, rather than the user having to flip the toggle off+on.
        reconcilePushSubscription().catch(() => {});
      } catch (err) {
        if (err.code === Parse.Error.INVALID_SESSION_TOKEN) {
          this.$store.dispatch("auth/sessionExpired");
        }
      }
    }
  },
  watch: {
    async $route() {
      if (this.isDesktopWide) return;
      const user = this.$store.getters["auth/currentUser"];
      if (user) {
        // Promise gates inside each setup function make these idempotent —
        // safe to call repeatedly; only the first call does real work.
        await this.setupNotificationSubscription(user);
        await this.setupCollabInviteSubscription(user);
        // F-19: idempotent — opens once after login, no-op on subsequent
        // navigations while the socket is alive.
        getSocket();
      }
      if (!user && this.subscription) {
        await this.subscription.unsubscribe();
        this.subscription = null;
        this.subscriptionSetup = null;
      }
      if (!user && this.collabInviteSubscription) {
        await this.collabInviteSubscription.unsubscribe();
        this.collabInviteSubscription = null;
        this.collabInviteSubscriptionSetup = null;
        this.collabInvite = null;
        teardownSocket();
      }
      if (!user) {
        // Wipe the PWA badge on logout so a fresh signin doesn't inherit
        // the previous user's count painted on the home-screen icon.
        clearBadge();
      }
    },
  },
  methods: {
    stripPwaParam() {
      // When Chrome opens the PWA via our ?_pwa redirect, clean that param
      // from the URL so it doesn't appear in the address bar or history.
      const url = new URL(window.location.href);
      if (url.searchParams.has("_pwa")) {
        url.searchParams.delete("_pwa");
        window.history.replaceState({}, "", url.toString());
      }
    },
    async maybeRedirectToPWA() {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
      if (isStandalone) return;
      if (!("getInstalledRelatedApps" in navigator)) return;
      if (sessionStorage.getItem("_scrawl_pwa_redirect")) return;
      try {
        const apps = await navigator.getInstalledRelatedApps();
        if (apps.length > 0) {
          sessionStorage.setItem("_scrawl_pwa_redirect", "1");
          // location.replace(same-url) is a reload — Chrome never intercepts
          // it as a scope navigation. Adding ?_pwa=<ts> makes it a genuinely
          // different URL so Chrome sees a real navigation within the PWA's
          // scope and opens the installed app instead.
          const url = new URL(window.location.href);
          url.searchParams.set("_pwa", Date.now());
          window.location.replace(url.toString());
        }
      } catch {
        // API unavailable — silent
      }
    },
    setupNotificationSubscription(user) {
      // Promise gate — first caller starts the work, concurrent callers
      // (e.g. created() and the $route watcher firing on the initial
      // redirect) all await the same in-flight promise instead of each
      // kicking off a fresh subscribe.
      if (this.subscriptionSetup) return this.subscriptionSetup;
      this.subscriptionSetup = (async () => {
        const { username } = user.attributes;
        const query = new Parse.Query("Notification");
        query.equalTo("to", username);
        this.subscription = await query.subscribe();
        this.subscription.on("create", (object) => this.onForegroundNotification(object));
      })();
      return this.subscriptionSetup;
    },
    setupCollabInviteSubscription(user) {
      // Same Promise gate as above. Without it, racing setups produced
      // two LQ subscriptions, each `open` handler firing `query.first()` —
      // showing as two POSTs to /api/classes/DrawingSession on every boot.
      if (this.collabInviteSubscriptionSetup) return this.collabInviteSubscriptionSetup;
      this.collabInviteSubscriptionSetup = (async () => {
        const { username } = user.attributes;
        const DrawingSession = Parse.Object.extend("DrawingSession");
        const query = new Parse.Query(DrawingSession);
        query.equalTo("recipient", username);
        query.equalTo("status", "pending");
        this.collabInviteSubscription = await query.subscribe();

        const showIfPending = (session) => {
          if (this.$route.name === "collab") return;
          if (!this.collabInvite) {
            this.collabInvite = {
              sessionId: session.id,
              initiator: session.get("initiator"),
            };
          }
        };

        this.collabInviteSubscription.on("create", showIfPending);

        const dismissIfStale = (session) => {
          if (this.collabInvite && this.collabInvite.sessionId === session.id) {
            this.collabInvite = null;
          }
        };
        this.collabInviteSubscription.on("leave", dismissIfStale);
        this.collabInviteSubscription.on("delete", dismissIfStale);
      })();
      return this.collabInviteSubscriptionSetup;
    },

    async acceptCollab() {
      const { sessionId } = this.collabInvite;
      this.collabInvite = null;
      try {
        await Parse.Cloud.run("respondToCollabSession", { sessionId, accept: true });
        this.$router.push(`/collab/${sessionId}`);
      } catch {
        // session may have expired — silently ignore
      }
    },

    async declineCollab() {
      const { sessionId } = this.collabInvite;
      this.collabInvite = null;
      try {
        await Parse.Cloud.run("respondToCollabSession", { sessionId, accept: false });
      } catch {
        // session may have already expired
      }
    },

    onSwUpdateAvailable(event) {
      this.swUpdateWaiting = event.detail && event.detail.waiting;
    },
    applySwUpdate() {
      const waiting = this.swUpdateWaiting;
      this.swUpdateWaiting = null;
      window.dispatchEvent(
        new CustomEvent("scrawl-sw-skip-waiting", { detail: { waiting } })
      );
    },
    onNetworkOnline() {
      this.$store.dispatch("network/setOnline");
    },
    onNetworkOffline() {
      this.$store.dispatch("network/setOffline");
    },
    onVisibilityChange() {
      if (document.visibilityState === "visible") {
        // User foregrounded the app (PWA brought back from recents,
        // tab re-focused, etc.). Clear any leftover OS notifications
        // since seeing the app is the implicit acknowledgement.
        this.clearActiveScrawlNotifications();
        // The wake lock is automatically released by the browser on
        // visibility change, so re-acquire it when we come back.
        this.acquireWakeLock();
      }
    },
    async acquireWakeLock() {
      if (!("wakeLock" in navigator)) return;
      try {
        this.wakeLock = await navigator.wakeLock.request("screen");
        this.wakeLock.addEventListener("release", () => {
          if (document.visibilityState === "visible") this.acquireWakeLock();
        });
      } catch {
        // Silently ignore — e.g. low battery rejection or unsupported device.
      }
    },
    async clearActiveScrawlNotifications() {
      if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
      try {
        const reg = await navigator.serviceWorker.ready;
        if (!reg || !reg.getNotifications) return;
        const notifs = await reg.getNotifications({ tag: "scrawl" });
        notifs.forEach((n) => n.close());
      } catch {
        /* SW not registered (dev mode), or browser blocked the call — silent */
      }
    },

    // Lean v1 (mocks/notifications-screen.html): foreground arrival is a
    // silent dot — never an in-app toast or OS banner. The OS banner only
    // fires when the app is closed (via SW push handler with foreground
    // suppression). All this handler does now is light the Feed-tab dot.
    onForegroundNotification(object) {
      const username = this.$store.getters["auth/username"];
      if (!username) return;
      const to = object.get && object.get("to");
      if (to && to !== username) return;
      // Skip lighting the dot if the user is already on /feed — they will
      // see the new arrival via the existing Inbox LiveQuery + "N new
      // scrawls" pill in Feed.vue. Mark as seen so the dot stays off.
      if (this.$route?.path === "/feed") return;
      this.$store.dispatch("feed/markHasNew");
    },
    async refreshFeedDotInitial(user) {
      const { username } = user.attributes;
      if (this.$route?.path === "/feed") return;
      try {
        const Inbox = Parse.Object.extend("Inbox");
        const q = new Parse.Query(Inbox);
        q.equalTo("toUser", username);
        const lastSeen = getLastFeedVisitMs(username);
        if (lastSeen > 0) q.greaterThan("createdAt", new Date(lastSeen));
        const count = await q.count();
        this.$store.dispatch("feed/setCount", count);
      } catch {
        /* network blip — Nav.vue will retry on its own mount */
      }
    },
  },
};
</script>

<style lang="scss">
#app {
  left: 0;
  right: 0;
  height: 100vh;
  position: fixed;
}

.collab-invite-overlay {
  @include bottom-sheet-overlay;
}

.collab-invite-card {
  @include bottom-sheet-card;
  text-align: center;
  // Override the mixin's default 32px top padding — the original layout
  // relies on the top spacing leaving room for the holographic icon-tile
  // below the drag handle.
  padding: 28px 24px calc(env(safe-area-inset-bottom, 0px) + 40px);

  // Holographic icon-tile — 70×70 rounded square with conic gradient,
  // glossy top highlight, and a soft drop shadow. Matches mock 11c.
  .collab-invite-icon {
    position: relative;
    width: 70px;
    height: 70px;
    border-radius: 24px;
    margin: 12px auto 14px;
    background: $holo;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.1em;
    color: #fff;
    box-shadow:
      0 12px 24px -8px rgba($bubblegum, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.7);
    text-shadow: 0 2px 8px rgba($shadow-purple, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 4px;
      left: 14%;
      right: 14%;
      height: 38%;
      border-radius: 16px;
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.75),
        rgba(255, 255, 255, 0)
      );
      pointer-events: none;
    }
  }

  .collab-invite-from {
    font-family: $font-display;
    font-size: 1.4em;
    font-weight: 700;
    letter-spacing: -0.01em;
    background: $holo-linear;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0 0 4px;
  }

  .collab-invite-msg {
    font-family: $font-body;
    font-size: 0.95em;
    color: $ink-soft;
    margin: 0 0 18px;
  }

  .collab-invite-actions {
    display: flex;
    gap: 12px;

    .collab-decline-btn {
      flex: 1;
      padding: 14px 20px;
      font-family: $font-body;
      font-size: 0.95em;
      font-weight: 700;
      color: $ink;
      background: $glass;
      backdrop-filter: $glass-blur-soft;
      -webkit-backdrop-filter: $glass-blur-soft;
      border: 1px solid $glass-stroke;
      border-radius: $radius-pill;
      box-shadow: $shadow-chip;
      cursor: pointer;
      transition: transform $dur-fast $ease-bubble, opacity $dur-fast $ease-soft;

      &:active { transform: scale(0.96); opacity: 0.85; }
    }

    .collab-accept-btn {
      @include bubble-cta;
      flex: 2;
      padding: 14px 20px;
      font-size: 0.95em;
    }
  }
}

.sw-update-overlay {
  @include bottom-sheet-overlay;
  z-index: 10000;
}

.sw-update-modal {
  @include bottom-sheet-card;
  text-align: center;

  .sw-update-icon {
    font-size: 28px;
    background: $holo-linear;
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    margin-bottom: 10px;
    animation: sparkle-shift 6s $ease-soft infinite;
  }

  .sw-update-title {
    font-family: $font-display;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: $ink;
    margin: 0 0 6px;
  }

  .sw-update-body {
    font-family: $font-body;
    font-size: 13px;
    color: $ink-soft;
    line-height: 1.5;
    margin: 0 0 18px;
  }

  .sw-update-cta {
    @include bubble-cta;
    width: 100%;
    padding: 14px;
  }
}

.sw-update-modal-enter-active {
  transition: opacity 0.25s $ease-soft;
  .sw-update-modal { transition: transform 0.32s $ease-bubble, opacity 0.25s $ease-soft; }
}
.sw-update-modal-leave-active {
  transition: opacity 0.2s $ease-soft;
  .sw-update-modal { transition: transform 0.2s $ease-soft, opacity 0.2s $ease-soft; }
}
.sw-update-modal-enter-from,
.sw-update-modal-leave-to {
  opacity: 0;
  .sw-update-modal { transform: translateY(100%); opacity: 0; }
}

@keyframes sparkle-shift {
  0%, 100% { background-position: 0% 50%; }
  50%       { background-position: 100% 50%; }
}

.offline-banner {
  // Glass-soft banner pinned at top-center (full-width minus 14px gutters
  // per the mock). Matches mock /mocks/redesign-4-soft-y2k-bubble.html
  // screen 23 (.offline-banner, lines 1611–1633): a softly rounded
  // rectangle, NOT a full pill — the long sentence reads better with
  // the wider chrome.
  position: fixed;
  left: 14px;
  right: 14px;
  top: calc(env(safe-area-inset-top, 0px) + 14px);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: $glass-strong;
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border: 1px solid $glass-edge;
  color: $ink-soft;
  font-family: $font-body;
  // Mock uses 18px corners — softer than a pill, rounder than a card.
  border-radius: 18px;
  // Stacked drop + chromed top + bubblegum reflection inset (recolored
  // to bubblegum tones — the mock's signature glow).
  box-shadow:
    0 12px 24px -12px rgba($shadow-purple, 0.35),
    inset 0 1px 0 #fff,
    inset 0 -4px 10px -4px rgba($bubblegum, 0.25);
  font-size: 12px;
  font-weight: 600;

  // Chromatic edge fringe — cyan hairline on the top edge.
  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 1px;
    border-radius: 18px;
    pointer-events: none;
    background: linear-gradient(90deg, transparent, $chrome-blue, transparent);
  }

  // Chromatic edge fringe — magenta hairline on the bottom edge.
  &::after {
    content: "";
    position: absolute;
    inset: auto 0 0 0;
    height: 1px;
    border-radius: 18px;
    pointer-events: none;
    background: linear-gradient(90deg, transparent, $bubblegum, transparent);
  }

  .offline-banner-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: $bubblegum-deep;
    // Outer ambient bloom (constant) so the dot reads as "live" even
    // mid-pulse; the keyframe layers an expanding bubblegum ring on top.
    box-shadow:
      0 0 10px rgba($bubblegum-deep, 0.7),
      0 0 0 0 rgba($bubblegum-deep, 0.55);
    animation: offline-banner-pulse 1.4s ease-out infinite;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }

  .offline-banner-msg {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
    z-index: 1;
  }
}

@keyframes offline-banner-pulse {
  0% {
    box-shadow:
      0 0 10px rgba($bubblegum-deep, 0.7),
      0 0 0 0 rgba($bubblegum-deep, 0.55);
  }
  70% {
    box-shadow:
      0 0 10px rgba($bubblegum-deep, 0.7),
      0 0 0 10px rgba($bubblegum-deep, 0);
  }
  100% {
    box-shadow:
      0 0 10px rgba($bubblegum-deep, 0.7),
      0 0 0 0 rgba($bubblegum-deep, 0);
  }
}

.offline-banner-enter-active,
.offline-banner-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.offline-banner-enter-from,
.offline-banner-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

@media (prefers-reduced-motion: reduce) {
  .offline-banner-dot { animation: none; }
  .offline-banner-enter-active,
  .offline-banner-leave-active { transition: opacity 0.22s ease; }
  .offline-banner-enter-from,
  .offline-banner-leave-to { transform: none; }
}
</style>
